"use client";

import { Card, CardBody, CardFooter, Image, Chip } from "@nextui-org/react";
import { WooCommerceProduct } from "@/types/woocommerce";

interface ProductCardProps {
  product: WooCommerceProduct;
}

export function ProductCard({ product }: ProductCardProps) {
  const price = parseFloat(product.price);
  const regularPrice = parseFloat(product.regular_price);
  const isOnSale = product.on_sale && price < regularPrice;

  return (
    <Card className="w-full h-full group">
      <CardBody className="p-0 overflow-hidden">
        <div className="relative aspect-square overflow-hidden bg-gray-100">
          {product.images && product.images.length > 0 ? (
            <Image
              src={product.images[0].src}
              alt={product.images[0].alt || product.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              removeWrapper
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <span className="text-gray-400">No Image</span>
            </div>
          )}
          
          {/* Sale Badge */}
          {isOnSale && (
            <Chip
              color="danger"
              size="sm"
              className="absolute top-2 left-2"
            >
              Sale
            </Chip>
          )}
          
          {/* Stock Status Badge */}
          <Chip
            color={product.stock_status === "instock" ? "success" : "warning"}
            size="sm"
            variant="flat"
            className="absolute top-2 right-2"
          >
            {product.stock_status === "instock" ? "In Stock" : "Out of Stock"}
          </Chip>
        </div>
      </CardBody>
      
      <CardFooter className="flex flex-col items-start gap-2 p-4">
        {/* Categories */}
        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.categories.slice(0, 2).map((category) => (
              <Chip key={category.id} size="sm" variant="flat" color="primary">
                {category.name}
              </Chip>
            ))}
          </div>
        )}
        
        {/* Product Name */}
        <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        
        {/* Short Description */}
        {product.short_description && (
          <p 
            className="text-sm text-gray-600 line-clamp-2"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}
        
        {/* Price */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xl font-bold">
            ${product.price || "0.00"}
          </span>
          {isOnSale && (
            <span className="text-sm text-gray-500 line-through">
              ${product.regular_price}
            </span>
          )}
        </div>
        
        {/* Rating */}
        {product.average_rating && parseFloat(product.average_rating) > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-yellow-500">★</span>
            <span className="text-sm">{product.average_rating}</span>
            <span className="text-sm text-gray-500">({product.rating_count} reviews)</span>
          </div>
        )}
        
              </CardFooter>
    </Card>
  );
}
